// 저장 안 한 편집을 두고 떠나는지 가르는 순수 판단 — 견적 편집기와 설정 저장 바가 같이 쓴다(훅은 hooks/use-leave-guard).

export interface LinkClick {
  button: number;
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
  defaultPrevented: boolean;
}

export interface LinkTarget {
  href: string;
  target: string;
  download: boolean;
}

// %-인코딩을 풀어 비교한다(예약 문자 &·/ 등은 decodeURI 가 안 푼다) — 대소문자·인코딩 여부만 다른 주소는 같은 화면. 깨진 인코딩은 원문 그대로.
const decoded = (text: string): string => {
  try {
    return decodeURI(text);
  } catch {
    return text;
  }
};

// 끝 슬래시는 떼고 본다(루트 「/」 는 그대로).
const pathOf = (url: URL): string => decoded(url.pathname).replace(/(.)\/+$/, "$1");

// 링크 확인에 「예」 한 뒤 이 시간 안의 beforeunload 는 다시 묻지 않는다 — 일반 <a> 는 전체 이동이라 브라우저가 한 번 더 묻는다.
export const LEAVE_PASS_MS = 3_000;

export const unloadPassed = (passAt: number | null, now: number): boolean =>
  passAt !== null && now >= passAt && now - passAt < LEAVE_PASS_MS;

// 두 주소가 같은 화면인가 — 경로·쿼리만 보고 해시는 뺀다.
const sameScreen = (a: URL, b: URL): boolean =>
  a.origin === b.origin && pathOf(a) === pathOf(b) && decoded(a.search) === decoded(b.search);

// 이 클릭이 같은 탭에서 앱 안 다른 화면으로 옮기는가 — 새 탭·다운로드·다른 출처(beforeunload 가 맡음)·같은 화면의 해시 이동은 아니다.
export const leavesInApp = (click: LinkClick, link: LinkTarget, here: string): boolean => {
  if (click.defaultPrevented || click.button !== 0) return false;
  if (click.metaKey || click.ctrlKey || click.shiftKey || click.altKey) return false;
  if (link.href === "" || link.download) return false;
  if (link.target !== "" && link.target !== "_self") return false;
  let from: URL;
  let to: URL;
  try {
    from = new URL(here);
    to = new URL(link.href, here);
  } catch {
    return false;
  }
  if (to.origin !== from.origin) return false;
  return !sameScreen(from, to);
};

export type PopDecision = "ask" | "pass";

// 뒤로·앞으로(popstate) 를 어떻게 다룰지 — 다른 화면이면 묻고, 같은 화면의 해시 이동은 그냥 보낸다.
export const popDecision = (s: { now: string; here: string }): PopDecision => {
  let now: URL;
  let here: URL;
  try {
    now = new URL(s.now);
    here = new URL(s.here);
  } catch {
    return "pass";
  }
  return sameScreen(now, here) ? "pass" : "ask";
};

// history.state 에 「편집 중인 자리」 표식을 붙이는 키 — Next 의 키(__NA·트리)는 그대로 두고 옆에 더한다.
export const LEAVE_STAMP_KEY = "__buildpyLeave";

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

export const withLeaveStamp = (state: unknown, token: string): Record<string, unknown> => ({
  ...(isRecord(state) ? state : {}),
  [LEAVE_STAMP_KEY]: token,
});

export const leaveStampOf = (state: unknown): string | null => {
  const value = isRecord(state) ? state[LEAVE_STAMP_KEY] : undefined;
  return typeof value === "string" ? value : null;
};

// Next 앱 라우터가 제 기록에 붙이는 키.
export const NEXT_HISTORY_KEY = "__NA";

// 제자리를 새로 쌓아도 되는가 — 이 키가 없으면 Next 의 pushState 가 떠나온 기록의 트리로 화면을 되돌려(RESTORE) 주소와 화면이 어긋난다.
export const canRestack = (state: unknown): boolean => isRecord(state) && Boolean(state[NEXT_HISTORY_KEY]);

// 되돌리는 history.go 가 기록 끝을 넘어 아무 일도 없었다고 보는 시간 — 같은 문서 안 이동의 popstate 는 보통 수십 ms 안에 온다.
export const RETURN_WAIT_MS = 1_000;

export interface BackGuardEnv {
  href: () => string;
  state: () => unknown;
  // 지금 기록의 state 만 바꾼다(주소는 그대로).
  replaceState: (state: Record<string, unknown>) => void;
  // 새 기록을 쌓는다(popstate 없음) — Navigation API 가 없을 때 제자리를 만드는 데 쓴다.
  pushState: (state: Record<string, unknown>, url: string) => void;
  go: (delta: number) => void;
  // Navigation API 의 기록 자리 — 없으면 null(iOS 26 전 Safari 등).
  entryIndex: () => number | null;
  confirm: (message: string) => boolean;
  // 되돌리기를 포기할 때 지금 주소를 라우터에 다시 알린다(합성 popstate).
  redeliver: () => void;
  timer: (fn: () => void, ms: number) => () => void;
}

export interface BackGuardOptions {
  token: string;
  enabled: () => boolean;
  message: () => string;
  onLeave: () => void;
}

export interface BackGuard {
  arm: () => void;
  onPop: (state: unknown) => "pass" | "block";
  dispose: () => void;
}

// 뒤로·앞으로 가기를 물어 취소하면 편집하던 자리로 되돌린다 — Navigation API 가 있으면 자리 차이만큼 한 번, 없으면 그 자리의 state·주소를 새로 쌓는다(다른 문서 기록으로 go 하지 않는다).
export const createBackGuard = (env: BackGuardEnv, opts: BackGuardOptions): BackGuard => {
  let here = env.href();
  let hereIndex = env.entryIndex();
  let hereState = withLeaveStamp(env.state(), opts.token);
  let stopWait: (() => void) | null = null;
  let redelivering = false;

  const arm = () => {
    if (stopWait) return;
    here = env.href();
    hereIndex = env.entryIndex();
    if (leaveStampOf(env.state()) !== opts.token) env.replaceState(withLeaveStamp(env.state(), opts.token));
    hereState = withLeaveStamp(env.state(), opts.token);
  };

  const stopSeek = () => {
    stopWait?.();
    stopWait = null;
  };

  const settleHere = () => {
    here = env.href();
    hereIndex = env.entryIndex();
    hereState = withLeaveStamp(env.state(), opts.token);
  };

  const giveUp = () => {
    stopSeek();
    settleHere();
    redelivering = true;
    try {
      env.redeliver();
    } finally {
      redelivering = false;
    }
  };

  // 자리 차이만큼 한 번 옮기고, 1초 안에 도착 popstate 가 없으면 포기하고 라우터에 넘긴다.
  const returnBy = (delta: number) => {
    stopSeek();
    let stop = (): void => undefined;
    stopWait = () => stop();
    const mine = stopWait;
    env.go(delta);
    stop = env.timer(() => {
      if (stopWait === mine) giveUp();
    }, RETURN_WAIT_MS);
  };

  const startReturn = () => {
    const now = env.entryIndex();
    const exact = hereIndex !== null && now !== null && now !== hereIndex;
    if (exact) return returnBy((hereIndex as number) - (now as number));
    // Next 키가 없으면 쌓지 않고 라우터에 넘긴다 — 편집은 잃어도 주소와 화면은 맞는다.
    if (!canRestack(hereState)) return giveUp();
    // 뒤로 취소면 원래 자리와 그 앞쪽 기록이 이 한 칸으로 바뀌고(원래 자리가 끝이면 길이 그대로), 앞으로 취소면 한 칸 늘어난다 — Next 는 __NA 가 있으면 원본 pushState 만 부른다.
    env.pushState(hereState, here);
    arm();
  };

  const onPop = (state: unknown): "pass" | "block" => {
    if (redelivering) return "pass";
    if (stopWait) {
      const now = env.entryIndex();
      if (now === hereIndex) {
        stopSeek();
        // 되돌아온 자리를 새 기준으로 — replaceState 로 주소가 바뀌었어도 다음 취소에서 두 번 묻지 않는다.
        arm();
        return "block";
      }
      if (now !== null && hereIndex !== null) {
        // 되돌리는 사이 또 옮겨졌으면 다시 차이만큼.
        returnBy(hereIndex - now);
        return "block";
      }
      stopSeek();
      settleHere();
      return "pass";
    }
    if (!opts.enabled()) return "pass";
    if (leaveStampOf(state) === opts.token) return "pass";
    if (popDecision({ now: env.href(), here }) === "pass") return "pass";
    if (env.confirm(opts.message())) {
      opts.onLeave();
      settleHere();
      return "pass";
    }
    startReturn();
    return "block";
  };

  return { arm, onPop, dispose: stopSeek };
};

export type PopHandler = (state: unknown) => "pass" | "block";

export interface PopHub {
  // 가드를 싣는다 — 돌려받은 함수로 내린다.
  add: (handler: PopHandler) => () => void;
  size: () => number;
}

const HUB_KEY = "__buildpyLeaveHub";

export type PopHubHost = Pick<EventTarget, "addEventListener"> & { [HUB_KEY]?: PopHub };

// 앱 시작 때 한 번 거는 popstate 리스너 — 라우터보다 먼저 등록돼야 막을 수 있어(같은 대상의 리스너 순서는 브라우저마다 다르다) 가드는 마운트 때 여기 싣기만 한다.
export const installLeavePopHub = (host: PopHubHost): PopHub => {
  const existing = host[HUB_KEY];
  if (existing) return existing;
  const handlers: PopHandler[] = [];
  const hub: PopHub = {
    add: (handler) => {
      handlers.push(handler);
      return () => {
        const at = handlers.indexOf(handler);
        if (at >= 0) handlers.splice(at, 1);
      };
    },
    size: () => handlers.length,
  };
  host[HUB_KEY] = hub;
  host.addEventListener(
    "popstate",
    (e) => {
      const state = (e as Event & { state?: unknown }).state;
      // 복사본으로 돈다 — 가드가 도중에 내려가도 순회가 흔들리지 않게.
      for (const handler of [...handlers]) {
        if (handler(state) === "block") {
          e.stopImmediatePropagation();
          return;
        }
      }
    },
    true
  );
  return hub;
};
